import { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

const NOTES_MIN = 5;
const NOTES_MAX = 1000;

// Backend CancelUploadDto still requires a non-null CancelReason enum. The UI
// no longer distinguishes technical vs non-technical, so every user-driven
// cancel is submitted as NON_TECHNICAL and the free-text reason travels in `notes`.
const REASON_NON_TECHNICAL = "NON_TECHNICAL";

export const CancelUploadDialog = ({
                                       open,
                                       packageName,
                                       onClose,
                                       onConfirm,
                                       className,
                                   }) => {
    const theme = useTheme();

    const [notes, setNotes] = useState("");

    useEffect(() => {
        if (open) {
            setNotes("");
        }
    }, [open]);

    const trimmedNotes = notes.trim();
    const hasValidNotes = trimmedNotes.length >= NOTES_MIN;

    const handleConfirm = () => {
        if (!hasValidNotes) return;

        onConfirm({
            reason: REASON_NON_TECHNICAL,
            notes: trimmedNotes,
        });
    };

    const textFieldSx = {
        "& .MuiOutlinedInput-root": {
            background: "rgba(255,255,255,0.04)",
            borderRadius: "10px",
            color: "#f7fafa",
            fontSize: "13px",

            "& fieldset": {
                borderColor: "rgba(255,255,255,0.16)",
            },

            "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.28)",
            },

            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.light,
            },
        },

        "& .MuiInputLabel-root": {
            color: "#f7fafaa6",
            fontSize: "13px",
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.light,
        },

        "& .MuiFormHelperText-root": {
            color: "#f7fafa66",
            fontSize: "11px",
            textAlign: "right",
            marginRight: 0,
        },
    };

    const notesHelperText = hasValidNotes
        ? `${notes.length}/${NOTES_MAX}`
        : `Minimum ${NOTES_MIN} characters · ${notes.length}/${NOTES_MAX}`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={className}
            sx={{
                "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(0,0,0,0.55)",
                },

                "& .MuiPaper-root": {
                    backgroundColor: theme.palette.primary.dark2,
                    backgroundImage: "none",
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    padding: "4px 2px",
                    width: "100%",
                    minWidth: "320px",
                    maxWidth: "420px",
                    margin: "12px",
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: 700,
                    padding: "14px 18px 2px",
                }}
            >
                Cancel upload?
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: "6px 18px 0 !important",
                }}
            >
                <Typography
                    sx={{
                        color: "#f7fafaa6",
                        fontSize: "12px",
                        lineHeight: "16px",
                        marginBottom: "10px",
                    }}
                >
                    {packageName ? (
                        <>
                            <Typography
                                component="span"
                                sx={{
                                    color: "#f7fafa",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                }}
                            >
                                {packageName}
                            </Typography>{" "}
                            — any progress will be lost.
                        </>
                    ) : (
                        "Any progress will be lost."
                    )}
                </Typography>

                <TextField
                    margin="dense"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    size="small"
                    required
                    label="Why are you cancelling?"
                    inputProps={{
                        maxLength: NOTES_MAX,
                    }}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    helperText={notesHelperText}
                    sx={textFieldSx}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    padding: "10px 18px 14px",
                    gap: "8px",
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderColor: "rgba(255,255,255,0.22)",
                        color: "rgba(255,255,255,0.92)",
                        fontWeight: 700,
                        fontSize: "12px",
                        padding: "6px 16px",
                        borderRadius: "10px",
                        textTransform: "none",

                        "&:hover": {
                            borderColor: "rgba(255,255,255,0.4)",
                            backgroundColor: "rgba(255,255,255,0.04)",
                        },
                    }}
                >
                    Keep uploading
                </Button>

                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!hasValidNotes}
                    data-testid="upload-confirm-cancel-button"
                    sx={{
                        backgroundColor: theme.palette.error.main,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "12px",
                        padding: "6px 16px",
                        borderRadius: "10px",
                        textTransform: "none",
                        boxShadow: "none",

                        "&:hover": {
                            backgroundColor: "#f24155",
                            boxShadow: "none",
                        },

                        "&.Mui-disabled": {
                            backgroundColor: "rgba(241,94,107,0.32)",
                            color: "rgba(255,255,255,0.6)",
                        },
                    }}
                >
                    Cancel upload
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancelUploadDialog;
